import React, { useState } from "react";
import { Table, Button } from "reactstrap";
import formatCurrency from "constants/formatCurrency";
import formatDate from "constants/formatDate";
import monthlyOrderStatusBadge from "constants/monthlyOrderStatusBadge";
import LongerTextTableCell from "./LongerTextTableCell";
import paymentTypeBadge from "constants/paymentTypeBadge";
import TablePaginationButtons from "./TablePaginationButtons";
import SortableTableHeader from "./SortableTableHeader";
import monthlyParkingTotalCalculations from "constants/monthlyParkingTotalCalculations";
import BarcodeCell from "./BarcodeCell";
import MonthlyParkingCustomerModal from "./MonthlyParkingCustomerModal";
import MonthlyParkingCustomerOrdersModal from "./MonthlyParkingCustomerOrdersModal";
import MonthlyParkingOrderUpdateModal from "./MonthlyParkingOrderUpdateModal";
import { createMonthlyParkingSubscriptionPendingRenewalOrder, updateMonthlyParkingSubscriptionPayment } from "helpers/fakebackend_helper";
import LineItemsCell from "./LineItemsCell";

const MonthlyOrdersTableBySubscriptionView = ({
    orders,
    search,
    filters,
    filterOrders,
    expandedLocation,
    setExpandedLocation,
    locationPages,
    setLocationPages,
    pageSize,
    setSortConfig,
    sortedOrders,
    onRefresh,
    forceHideZero = false
}) => {
    const user = JSON.parse(localStorage.getItem("authUser"));
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [hideZeros, setHideZeros] = useState(forceHideZero || false);
    const [isOrdersModalOpen, setIsOrdersModalOpen] = useState(false);
    const [customerOrders, setCustomerOrders] = useState([]);
    const [editSubscriptionModal, setEditSubscriptionModal] = useState({ open: false, subscription: null });

    const filteredAndSortedLocations = orders
        .filter((loc) => {
            const q = search.trim().toLowerCase();
            if (!q) return true;

            const locationMatch = (loc.name || "").toLowerCase().includes(q);
            const subscriptionMatch = (loc.subscriptions || []).some((s) =>
                ((s.customer_first_name + " " + s.customer_last_name) || "").toLowerCase().includes(q) ||
                (s.customer_email || "").toLowerCase().includes(q) ||
                (s.customer_phone || "").toLowerCase().includes(q) ||
                (s.license_plates || "").toLowerCase().includes(q) ||
                (s.car_models || "").toLowerCase().includes(q)
            );

            return locationMatch || subscriptionMatch;
        })
        .map((loc) => {
            const filteredSubs = filterOrders(
                (loc.subscriptions || []).map((s) => ({ ...s, locationName: loc.name }))
            );

            const { totalCash, totalCheque, totalStripe, totalQbo, totalUnknown, totalCollected } = monthlyParkingTotalCalculations(filteredSubs);

            return {
                ...loc,
                filteredSubs,
                totalCash,
                totalCheque,
                totalStripe,
                totalQbo,
                totalUnknown,
                totalCollected,
            };
        });

    const sortedLocations = sortedOrders(filteredAndSortedLocations).filter((loc) => {
        if (!hideZeros) return true;
        const { totalCash, totalCheque, totalStripe, totalQbo, totalUnknown } = monthlyParkingTotalCalculations(loc.filteredSubs);
        return totalCash + totalCheque + totalStripe + totalQbo + totalUnknown > 0;
    });

    return (
        <>
            <Table className="align-middle table-hover table-nowrap mb-0" responsive>
                <SortableTableHeader
                    columns={[
                        { label: "Location" },
                        { label: "View Subscriptions" },
                    ]}
                    onSort={setSortConfig}
                    showHideZero={true}
                    hideZeros={hideZeros}
                    onToggleHideZero={() => setHideZeros(!hideZeros)}
                />
                <tbody>
                    {sortedLocations.map((loc) => <React.Fragment key={loc.id}>
                        <tr>
                            <td
                                style={{ cursor: "pointer" }}
                                onClick={() =>
                                    setExpandedLocation(
                                        expandedLocation === loc.id ? null : loc.id
                                    )
                                }
                            >
                                {loc.name}{" "}
                                <span className="text-muted">
                                    (
                                    {
                                        Array.from(
                                            new Set(
                                                (loc.filteredSubs || [])
                                                    .map((s) => (s.customer_email || "").toLowerCase())
                                                    .filter((e) => e)
                                            )
                                        ).length
                                    }{" "}
                                    Customers - {loc.filteredSubs.length} Subs)
                                </span>
                            </td>
                            <td>
                                <Button
                                    className={expandedLocation === loc.id ? "warning" : "button-gradient-bg"}
                                    onClick={() =>
                                        setExpandedLocation(
                                            expandedLocation === loc.id ? null : loc.id
                                        )
                                    }
                                >
                                    {expandedLocation === loc.id
                                        ? "Hide Subscriptions"
                                        : "View Subscriptions"}
                                </Button>
                            </td>
                        </tr>

                        {expandedLocation === loc.id && (
                            <tr>
                                <td colSpan={7}>
                                    <Table className="align-middle table-sm" responsive>
                                        <thead className="table-light">
                                            <tr>
                                                <th>ID</th>
                                                <th>Date Created</th>
                                                <th>Next Payment</th>
                                                <th>Customer</th>
                                                <th>Items</th>
                                                <th>Orders</th>
                                                <th>Payment Type</th>
                                                <th>Status</th>
                                                <th>Total</th>
                                                <th>License Plates</th>
                                                <th>Car Models</th>
                                                {filters.showBarcode && <th>Barcode</th>}
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loc.filteredSubs
                                                .sort((a, b) => new Date(b.date_created) - new Date(a.date_created))
                                                .slice(
                                                    filters.pagination
                                                        ? ((locationPages[loc.id] || 1) - 1) * pageSize
                                                        : 0,
                                                    filters.pagination
                                                        ? (locationPages[loc.id] || 1) * pageSize
                                                        : undefined)
                                                .map((s) => (
                                                    <tr key={s.id}>
                                                        <td>{s.subscription_id}</td>
                                                        <td>{formatDate(s.date_created)}</td>
                                                        <td>{s.next_payment_date ? formatDate(s.next_payment_date) : "-"}</td>
                                                        <LongerTextTableCell
                                                            value={
                                                                (s.customer_first_name + " " + s.customer_last_name) +
                                                                ((s.customer_company_name &&
                                                                    s.customer_company_name !== "null" &&
                                                                    s.customer_company_name !== "")
                                                                    ? " - " + s.customer_company_name
                                                                    : "")
                                                            }
                                                            maxLength={15}
                                                            onClick={() => setSelectedCustomer(s)}
                                                        />
                                                        <LineItemsCell
                                                            line_items_json={s.line_items_json}
                                                            quantity={s.quantity}
                                                            item_type={s.item_type}
                                                        />
                                                        <td
                                                            style={{ cursor: "pointer", textDecoration: "underline" }}
                                                            onClick={() => {
                                                                const customerOrdersData = (loc.orders || []).map(o => ({
                                                                    ...o,
                                                                    locationName: loc.name,
                                                                    locationId: loc.id
                                                                })).filter(
                                                                    (o) =>
                                                                        o.customer_email &&
                                                                        s.customer_email &&
                                                                        o.customer_email.toLowerCase() === s.customer_email.toLowerCase()
                                                                );

                                                                setCustomerOrders(customerOrdersData);
                                                                setIsOrdersModalOpen(true);
                                                            }}
                                                        >
                                                            {loc.orders
                                                                ? loc.orders.filter(
                                                                    (o) =>
                                                                        o.customer_email &&
                                                                        s.customer_email &&
                                                                        o.customer_email.toLowerCase() === s.customer_email.toLowerCase()
                                                                ).length
                                                                : 0}
                                                        </td>
                                                        <td>{paymentTypeBadge(s.payment_type, null)}</td>
                                                        <td
                                                            style={{ cursor: "pointer" }}
                                                            onClick={() => setEditSubscriptionModal({ open: true, subscription: { ...s, editType: "subscription-status" } })}
                                                        >{monthlyOrderStatusBadge(s.status)}</td>
                                                        <td>${formatCurrency(s.total)}</td>
                                                        <LongerTextTableCell value={s.license_plates} maxLength={10} />
                                                        <LongerTextTableCell value={s.car_models} maxLength={10} />
                                                        <BarcodeCell barcode={s.barcode_number} show={filters.showBarcode} />
                                                        <td>
                                                            <i
                                                                className="bx bx-pause"
                                                                style={{ cursor: "pointer", fontSize: 30, color: "red" }}
                                                                onClick={() => setEditSubscriptionModal({ open: true, subscription: { ...s, editType: "pause" } })}
                                                            ></i>
                                                            <i
                                                                className="bx bx-plus"
                                                                style={{ cursor: "pointer", fontSize: 23, color: "green" }}
                                                                onClick={() => setEditSubscriptionModal({ open: true, subscription: { ...s, editType: "pending-renewal-order" } })}
                                                            ></i>
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </Table>

                                    {filters.pagination && loc.filteredSubs.length > pageSize && (
                                        <TablePaginationButtons
                                            currentPage={locationPages[loc.id] || 1}
                                            totalItems={loc.filteredSubs.length}
                                            pageSize={pageSize}
                                            onPageChange={(newPage) =>
                                                setLocationPages((prev) => ({ ...prev, [loc.id]: newPage }))
                                            }
                                        />
                                    )}
                                </td>
                            </tr>
                        )}
                    </React.Fragment>)}
                </tbody>
            </Table>

            <MonthlyParkingCustomerOrdersModal
                isOpen={isOrdersModalOpen}
                onClose={() => setIsOrdersModalOpen(false)}
                orders={customerOrders}
                filters={filters}
            />

            <MonthlyParkingCustomerModal
                customer={selectedCustomer}
                onClose={() => setSelectedCustomer(null)}
                onEdit={async (data) => {
                    setSelectedCustomer(null);
                    openEditModal(data);
                }}
            />

            <MonthlyParkingOrderUpdateModal
                isOpen={editSubscriptionModal.open}
                toggle={() => setEditSubscriptionModal({ open: false, subscription: null })}
                order={editSubscriptionModal.subscription}
                onSave={async (data) => {
                    if (editSubscriptionModal.subscription?.editType === "pause") {
                        await updateMonthlyParkingSubscriptionPayment(
                            user.token,
                            editSubscriptionModal.subscription.id,
                            { next_payment_date: data.next_payment_date }
                        );
                        onRefresh();
                    } else if (editSubscriptionModal.subscription?.editType === "pending-renewal-order") {
                        await createMonthlyParkingSubscriptionPendingRenewalOrder(
                            user.token,
                            editSubscriptionModal.subscription.subscription_id,
                            { pending_renewal_order_date: data.pending_renewal_order_date }
                        );
                        onRefresh();
                    }
                    else if (editSubscriptionModal.subscription?.editType === "subscription-status") {
                        await updateMonthlyParkingSubscriptionPayment(
                            user.token,
                            editSubscriptionModal.subscription.id,
                            { status: data.status }
                        );
                        onRefresh();
                    }
                    else if (editSubscriptionModal.subscription?.editType === "subscription-payment-method") {
                        // IT IS NOT WORKING, COME BACK TO THIS LATER, WORDPRESS DOESNT LET ME CHANGE THE STATUS FROM API
                        // console.log("datapm: ", data)
                        // await updateMonthlyParkingSubscriptionPayment(
                        //     user.token,
                        //     editSubscriptionModal.subscription.id,
                        //     { payment_type: data.payment_type }
                        // );
                        // onRefresh();
                    }
                    setEditSubscriptionModal({ open: false, subscription: null });
                }}
            />
        </>
    );
};

export default MonthlyOrdersTableBySubscriptionView;